import React, { useState, useEffect, useRef } from "react";
import "../../Styles/Account.css";
import "../../Styles/AccountProfile.css";
import KYC from "./KYC";
import { showError, showSuccess } from "../../utils/Toast";
import Modal from "../Modal/Modal";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/AxiosConfig";
import OfferCountdown from "../Common/OfferCountdown";
import DownloadInvoiceButton from "../Common/DownloadInvoiceButton";
import MyOrders from "./MyOrders";
import { FiBriefcase, FiEdit2, FiFileText, FiHeart, FiHome, FiLogOut, FiMapPin, FiPhone, FiPlus, FiRefreshCw, FiStar, FiTrash2, FiUser } from "react-icons/fi";
import {
  DEFAULT_PRODUCT_IMAGE,
  isProductOutOfStock,
  withDefaultProductImage,
} from "../../utils/productPresentation";
import { formatAED } from "../../utils/currency";
import { getOrderPricingSummary } from "../../utils/orderSummary";
import { resolveReviewDisplayState } from "../../utils/reviewEligibility";
import { isUserLoggedIn, openAuthModal } from "../../utils/authModal";
import {
  getViewportIsMobile,
  setStorageItem,
} from "../../utils/browser";
import { clearBuyerSession, getBuyerToken } from "../../utils/authStorage";

const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&]).{8,}$/;
const PASSWORD_VALIDATION_MESSAGE =
  "Password must be at least 8 characters and include letters, numbers and special characters.";
const WISHLIST_STORAGE_KEY = "wishlist_product_ids";
const FAVORITES_STORAGE_KEY = "favorites";

const parsePositiveInteger = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
};

const normalizeWishlistIds = (items = []) =>
  Array.from(
    new Set(
      (Array.isArray(items) ? items : [])
        .map((item) => parsePositiveInteger(item?.product_id ?? item?.id))
        .filter((value) => value > 0),
    ),
  );

const syncWishlistStorage = (items = []) => {
  const ids = normalizeWishlistIds(items);
  const serializedIds = JSON.stringify(ids);

  setStorageItem(WISHLIST_STORAGE_KEY, serializedIds);
  setStorageItem(FAVORITES_STORAGE_KEY, serializedIds);
};

const normalizeWishlistActiveOffer = (item) => {
  const source =
    item?.active_offer ||
    (item?.is_offer_active
      ? {
          offer_id: item?.offer_id,
          product_id: item?.product_id,
          original_price: item?.original_price,
          offer_price: item?.offer_price,
          offer_discount_amount: item?.offer_discount_amount,
          offer_title: item?.offer_title,
          offer_message: item?.offer_message,
          offer_end_at: item?.offer_end_at,
          remaining_seconds: item?.remaining_seconds,
        }
      : null);

  if (!source || typeof source !== "object") {
    return null;
  }

  const offerId = parsePositiveInteger(source?.offer_id ?? source?.offerId);
  const productId = parsePositiveInteger(
    source?.product_id ?? source?.productId ?? item?.product_id,
  );
  const originalPrice = Number(
    source?.original_price ?? source?.originalPrice ?? 0,
  );
  const offerPrice = Number(source?.offer_price ?? source?.offerPrice ?? 0);
  const discountAmount = Number(
    source?.offer_discount_amount ??
      source?.offerDiscountAmount ??
      source?.discount_amount ??
      source?.discountAmount ??
      Math.max(originalPrice - offerPrice, 0),
  );
  const remainingSeconds = Number(
    source?.remaining_seconds ?? source?.remainingSeconds,
  );

  if (offerId <= 0 || productId <= 0 || originalPrice <= 0 || offerPrice <= 0) {
    return null;
  }

  return {
    offer_id: offerId,
    product_id: productId,
    original_price: originalPrice,
    offer_price: offerPrice,
    offer_discount_amount: Number.isFinite(discountAmount)
      ? discountAmount
      : Math.max(originalPrice - offerPrice, 0),
    offer_title: String(
      source?.offer_title ?? source?.offerTitle ?? "",
    ).trim(),
    offer_message: String(
      source?.offer_message ?? source?.offerMessage ?? "",
    ).trim(),
    offer_end_at:
      source?.offer_end_at ??
      source?.offerEndAt ??
      source?.to_date ??
      source?.toDate ??
      source?.end_date ??
      source?.endDate ??
      source?.ends_at ??
      source?.endsAt ??
      null,
    remaining_seconds: Number.isFinite(remainingSeconds)
      ? remainingSeconds
      : null,
  };
};

const getWishlistOfferEndTime = (activeOffer) =>
  activeOffer?.offer_end_at ||
  activeOffer?.to_date ||
  activeOffer?.end_date ||
  activeOffer?.ends_at ||
  null;

export default function Account({
  allorderdata,
  getallorderloading = false,
  getallordererror = null,
  wishlistData,
  insertCartRequest,
  removewishlistrequest,
  getwishlistRequest,
  getallorderRequest,
  addAddressRequest,
  addAddressLoading = false,
  addAddressData,
  addAddressError,
  getaddressData,
  getaddressLoading = false,
  getaddressError,
  getaddressRequest,
  insertprofileRequest,
  insertprofileloading = false,
  insertprofiledata,
  insertprofileerror,
  getprofiledataData = [],
}) {
  const [isMobile, setIsMobile] = useState(false);
  const wishlistExpireRefreshesRef = useRef(new Set());
  const accountNavRef = useRef(null);

  // Check screen size for mobile
  useEffect(() => {
    setIsMobile(getViewportIsMobile());

    const handleResize = () => {
      setIsMobile(getViewportIsMobile());
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!Array.isArray(wishlistData)) {
      return;
    }

    syncWishlistStorage(wishlistData);
    wishlistExpireRefreshesRef.current.clear();
  }, [wishlistData]);

  const sidebarItems = [
    {
      icon: <FiFileText />,
      title: "My Orders",
      key: "orders",
    },
    {
      icon: <FiUser />,
      title: "My Profile",
      key: "profile",
    },
    {
      icon: <FiHeart />,
      title: "Wishlist",
      key: "wishlist",
    },
    {
      icon: <FiMapPin />,
      title: "Saved Addresses",
      key: "addresses",
    },
    {
      icon: <FiLogOut />,
      title: "Logout",
      key: "logout",
    },
  ];


  const [showKYC, setShowKYC] = useState(false);
  const [cardName, setCardName] = useState("profile");
  useEffect(() => {
    const nav = accountNavRef.current;
    const active = nav?.querySelector(".acct-active");
    if (nav && active && nav.scrollWidth > nav.clientWidth) {
      nav.scrollTo({ left: active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2, behavior: "auto" });
    }
  }, [cardName]);
  const [copied, setCopied] = useState(false);
  const referralLink = "https://example.com/referral?code=ABC123";
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [profileData, setProfileData] = useState([]);
  const [isSyncingOrders, setIsSyncingOrders] = useState(false);
  const [lastOrdersSyncAt, setLastOrdersSyncAt] = useState(null);
  const [orderSearchInput, setOrderSearchInput] = useState("");
  const [appliedOrderSearch, setAppliedOrderSearch] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  // Return product states
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedReturnItem, setSelectedReturnItem] = useState(null);
  const [returnFormData, setReturnFormData] = useState({
    reason: "",
    description: "",
    images: [],
    return_type: "refund"
  });
  const [returnImages, setReturnImages] = useState([]);
  const [isSubmittingReturn, setIsSubmittingReturn] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReviewItem, setSelectedReviewItem] = useState(null);
  const [reviewFormData, setReviewFormData] = useState({
    rating: 0,
    review_text: "",
  });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewOverrides, setReviewOverrides] = useState({});

  const performLogout = () => {
    clearBuyerSession();
    showSuccess("Logged out successfully");

    window.setTimeout(() => {
      window.location.href = "/";
    }, 250);
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleConfirmLogout = () => {
    setShowLogoutConfirm(false);
    performLogout();
  };

  const handleSidebarItemClick = (itemKey) => {
    if (itemKey === "logout") {
      handleLogoutClick();
      return;
    }

    setCardName(itemKey);
    setShowKYC(false);
  };

  useEffect(() => {
    if (location.state && location.state.navigationname) {
      setCardName(location.state.navigationname);
    } else {
      setCardName("profile");
    }
  }, [location]);

  const [addressForm, setAddressForm] = useState({
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
  });

  const UAE_EMIRATES = [
    "Abu Dhabi",
    "Dubai",
    "Sharjah",
    "Ajman",
    "Fujairah",
    "Ras Al Khaimah",
    "Umm Al Quwain",
  ];

  const UAE_CITIES_BY_EMIRATE = {
    "Abu Dhabi": ["Abu Dhabi", "Al Ain", "Al Dhafra"],
    Dubai: ["Dubai"],
    Sharjah: ["Sharjah", "Khor Fakkan", "Dibba Al-Hisn", "Kalba"],
    Ajman: ["Ajman", "Manama", "Masfout"],
    Fujairah: ["Fujairah", "Dibba Al-Fujairah"],
    "Ras Al Khaimah": ["Ras Al Khaimah", "Al Jazirah Al Hamra"],
    "Umm Al Quwain": ["Umm Al Quwain"],
  };

  const getCitiesForEmirate = (emirate) =>
    UAE_CITIES_BY_EMIRATE[emirate] || [];
  const [profileForm, setProfileForm] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
  });
  const [changePasswordForm, setChangePasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [profileErrors, setProfileErrors] = useState({});
  const [profileWasSubmitted, setProfileWasSubmitted] = useState(false);
  const [addressRows, setAddressRows] = useState([]);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressActionId, setAddressActionId] = useState(null);
  const [isAddressSaving, setIsAddressSaving] = useState(false);
  const [addressActionError, setAddressActionError] = useState("");
  const pendingAddressRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(getaddressData?.data)) {
      setAddressRows(getaddressData.data.filter((address) => address?.profile_id && String(address?.address || "").trim()));
    }
  }, [getaddressData]);

  useEffect(() => {
    if (!pendingAddressRef.current) return;
    if (addAddressData?.success) {
      const added = addAddressData.data || {};
      setAddressRows((previous) => [...previous, {
        ...pendingAddressRef.current,
        ...added,
        profile_id: added.address_id,
        alt_phone: pendingAddressRef.current.phone,
      }]);
      showSuccess(addAddressData.message || "Address added successfully");
      pendingAddressRef.current = null;
      setShowAddAddress(false);
      setAddressForm({ address_line1: "", address_line2: "", city: "", state: "", pincode: "", phone: "" });
      getaddressRequest();
    } else if (addAddressError) {
      pendingAddressRef.current = null;
      setAddressActionError(addAddressError);
    }
  }, [addAddressData, addAddressError, getaddressRequest]);

  useEffect(() => {
    if (getprofiledataData?.length > 0) {
      setProfileData(getprofiledataData[0]);
      setProfileForm({
        firstname: getprofiledataData[0].first_name || "",
        lastname: getprofiledataData[0].last_name || "",
        email: getprofiledataData[0].user_email || "",
        phone: getprofiledataData[0].phone || "",
      });
    }
  }, [getprofiledataData]);

  useEffect(() => {
    if (!allorderdata) return;
    if (isSyncingOrders) {
      setIsSyncingOrders(false);
    }
    if (!lastOrdersSyncAt || isSyncingOrders) {
      setLastOrdersSyncAt(new Date());
    }
  }, [allorderdata, isSyncingOrders, lastOrdersSyncAt]);

  useEffect(() => {
    if (!getallorderloading) {
      setIsSyncingOrders(false);
    }
  }, [getallorderloading]);

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "delivered":
        return "✓";
      case "processing":
        return "⟳";
      case "shipped":
        return "🚚";
      case "cancelled":
        return "✕";
      case "pending":
        return "⏳";
      default:
        return "•";
    }
  };

  const getOrderItemName = (item) =>
    item?.product?.name ||
    item?.product_snapshot?.name ||
    item?.product_name ||
    "Product";

  const getOrderItemImage = (item) =>
    withDefaultProductImage(
      item?.product?.image_url ||
        item?.product?.image_urls?.[0] ||
        item?.product_snapshot?.image_url ||
        item?.product_image
    );

  const STATUS_LABELS = {
    pending: "Pending",
    placed: "Order Placed",
    confirmed: "Confirmed",
    accepted: "Accepted",
    processing: "Processing",
    packed: "Packed",
    ready_to_ship: "Ready to Ship",
    shipment_created: "Shipment Created",
    picked_up: "Picked Up",
    dispatched: "Shipped",
    in_transit: "In Transit",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered",
    unavailable: "Unavailable",
    cancelled: "Cancelled",
    returned: "Returned",
    refunded: "Refunded",
  };

  const PROCESSING_FILTER_STATUSES = new Set([
    "processing",
    "packed",
    "ready_to_ship",
  ]);

  const SHIPPED_FILTER_STATUSES = new Set([
    "shipped",
    "shipment_created",
    "picked_up",
    "dispatched",
    "in_transit",
    "out_for_delivery",
  ]);

  const TIMELINE_STAGES = [
    { key: "placed", label: "Order Placed" },
    { key: "accepted", label: "Accepted" },
    { key: "processing", label: "Processing" },
    { key: "packed_shipped", label: "Packed / Shipped" },
    { key: "delivered", label: "Delivered" },
  ];

  const TERMINAL_TIMELINE_LABELS = {
    cancelled: "Cancelled",
    rejected: "Rejected",
    returned: "Returned",
    refunded: "Refunded",
    failed: "Failed",
    failed_delivery: "Failed Delivery",
    unavailable: "Unavailable",
  };

  const normalizeStatusKey = (status) => {
    const normalized = String(status || "pending")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

    if (normalized === "order_placed") return "placed";
    if (normalized === "accept") return "accepted";
    return normalized;
  };

  const getAccountPaymentLabel = (order) => {
    const paymentStatus = normalizeStatusKey(order?.payment_status);
    const paymentMethod = normalizeStatusKey(order?.payment_method);

    if (
      paymentStatus === "paid" ||
      paymentStatus === "completed" ||
      paymentStatus === "success"
    ) {
      return paymentMethod === "cod" ? "Cash on Delivery - Paid" : "Paid";
    }

    if (paymentMethod === "cod") {
      return "Cash on Delivery";
    }

    return "Pending Payment";
  };

  const getAccountShippingAddress = (order) => {
    const shippingAddress = order?.shipping_address;
    if (!shippingAddress || typeof shippingAddress !== "object") {
      return "Saved Address";
    }

    const parts = [
      shippingAddress.name,
      shippingAddress.address_line1,
      shippingAddress.address_line2,
      shippingAddress.city,
      shippingAddress.state,
      shippingAddress.country,
      shippingAddress.postal_code || shippingAddress.pincode,
      shippingAddress.phone,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    return parts.join(", ") || "Saved Address";
  };

  const getAccountCarrierLabel = (order) =>
    order?.carrier_name ||
    order?.tracking_summary?.provider_name ||
    "Standard Courier";

  const getReadableStatus = (status) => {
    const normalizedStatus = normalizeStatusKey(status);
    return (
      STATUS_LABELS[normalizedStatus] ||
      normalizedStatus
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ") ||
      "Pending"
    );
  };

  const getStatusClassName = (status) =>
    normalizeStatusKey(status).replace(/_/g, "-");

  const isAnimatedStatus = (status) =>
    normalizeStatusKey(status) === "processing";

  const matchesOrderFilter = (status, selectedFilter) => {
    const normalizedStatus = normalizeStatusKey(status);
    const normalizedFilter = normalizeStatusKey(selectedFilter);

    if (normalizedFilter === "all") return true;
    if (normalizedFilter === "processing") {
      return PROCESSING_FILTER_STATUSES.has(normalizedStatus);
    }
    if (normalizedFilter === "dispatched") {
      return SHIPPED_FILTER_STATUSES.has(normalizedStatus);
    }

    return normalizedStatus === normalizedFilter;
  };

  const getTimelineStageKey = (status) => {
    const normalizedStatus = normalizeStatusKey(status);

    if (["placed", "pending", "pending_payment", "confirmed"].includes(normalizedStatus)) {
      return "placed";
    }
    if (normalizedStatus === "accepted") {
      return "accepted";
    }
    if (normalizedStatus === "processing") {
      return "processing";
    }
    if (
      [
        "packed",
        "ready_to_ship",
        "shipment_created",
        "picked_up",
        "dispatched",
        "in_transit",
        "out_for_delivery",
        "shipped",
      ].includes(normalizedStatus)
    ) {
      return "packed_shipped";
    }
    if (normalizedStatus === "delivered") {
      return "delivered";
    }

    return null;
  };

  const hasReachedProcessingStage = (status) => {
    const normalizedStatus = normalizeStatusKey(status);
    return (
      PROCESSING_FILTER_STATUSES.has(normalizedStatus) ||
      SHIPPED_FILTER_STATUSES.has(normalizedStatus) ||
      normalizedStatus === "delivered"
    );
  };

  const hasReachedShipmentStage = (status) => {
    const normalizedStatus = normalizeStatusKey(status);
    return (
      SHIPPED_FILTER_STATUSES.has(normalizedStatus) ||
      normalizedStatus === "delivered"
    );
  };

  const getTimelineStatusIcon = (status) => {
    const normalizedStatus = normalizeStatusKey(status);
    if (normalizedStatus === "delivered") return "D";
    if (normalizedStatus === "accepted") return "A";
    if (["processing", "packed", "ready_to_ship"].includes(normalizedStatus)) {
      return "P";
    }
    if (normalizedStatus === "packed_shipped") return "S";
    if (SHIPPED_FILTER_STATUSES.has(normalizedStatus)) return "S";
    if (
      ["cancelled", "returned", "rejected", "failed", "unavailable"].includes(
        normalizedStatus
      )
    ) {
      return "X";
    }
    if (
      ["placed", "pending", "pending_payment", "confirmed"].includes(
        normalizedStatus
      )
    ) {
      return "O";
    }
    return "•";
  };

  const formatTimelineTimestamp = (value) => {
    if (!value) return "";

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    const datePart = parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
    const timePart = parsedDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${datePart} ${timePart}`;
  };

  const buildOrderTimeline = (status, createdAt) => {
    const normalizedStatus = normalizeStatusKey(status);
    const currentStageKey = getTimelineStageKey(normalizedStatus) || "placed";
    const currentStageIndex = Math.max(
      TIMELINE_STAGES.findIndex((stage) => stage.key === currentStageKey),
      0
    );
    const terminalLabel = TERMINAL_TIMELINE_LABELS[normalizedStatus];

    if (terminalLabel) {
      return [
        {
          key: "placed",
          status: "Order Placed",
          date: formatTimelineTimestamp(createdAt),
          completed: true,
          active: false,
          icon: getTimelineStatusIcon("placed"),
          animated: false,
        },
        {
          key: normalizedStatus,
          status: terminalLabel,
          date: "",
          completed: true,
          active: true,
          icon: getTimelineStatusIcon(normalizedStatus),
          animated: false,
        },
      ];
    }

    return TIMELINE_STAGES.map((stage, index) => ({
      key: stage.key,
      status: stage.label,
      date: index === 0 ? formatTimelineTimestamp(createdAt) : "",
      completed: index < currentStageIndex,
      active: index === currentStageIndex,
      icon: getTimelineStatusIcon(stage.key),
      animated: index === currentStageIndex && stage.key === "processing",
    }));
  };

  const mapOrderTimeline = (timeline, fallbackStatus, createdAt) => {
    if (!Array.isArray(timeline) || !timeline.length) {
      return buildOrderTimeline(fallbackStatus, createdAt);
    }

    return timeline.map((step) => {
      const normalizedStepKey = normalizeStatusKey(step.key || step.status);
      return {
        key: normalizedStepKey,
        status: step.label || getReadableStatus(normalizedStepKey),
        date: formatTimelineTimestamp(step.timestamp),
        completed: Boolean(step.completed),
        active: Boolean(step.active),
        icon: getTimelineStatusIcon(normalizedStepKey),
        animated:
          Boolean(step.active) && getTimelineStageKey(normalizedStepKey) === "processing",
      };
    });
  };

  const getReviewOverrideKey = (orderId, itemId, productId) =>
    `${orderId}:${itemId || productId}`;

  const resetReviewModal = () => {
    setShowReviewModal(false);
    setSelectedReviewItem(null);
    setReviewFormData({
      rating: 0,
      review_text: "",
    });
    setIsSubmittingReview(false);
  };

  const openReviewModal = (order, item) => {
    setSelectedReviewItem({
      order_id: order.order_id,
      order_number: order.id,
      order_item_id: item.item_id,
      product_id: item.product_id,
      product_name: item.name,
      review_submitted: Boolean(item.review_submitted),
      review_id: item.review_id || null,
      review_rating: item.review_rating || null,
      review_status: item.review_status || null,
    });
    setReviewFormData({
      rating: Number(item.review_rating || 0),
      review_text: "",
    });
    setShowReviewModal(true);
  };

  const orders = (Array.isArray(allorderdata) ? allorderdata : []).map((order) => {
    const orderItems = Array.isArray(order.items) ? order.items : [];
    const pricingSummary = getOrderPricingSummary(order);
    const orderDate = new Date(order.created_at);
    const formattedDate = orderDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const formattedTime = orderDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const formattedTimestamp = `${formattedDate} ${formattedTime}`;
    const normalizedStatus = normalizeStatusKey(
      order.display_status ||
        order.latest_status ||
        order.status ||
        order.order_status ||
        order.delivery_status
    );

    return {
      id: order.order_number || order.order_id,
      order_id: order.order_id,
      order_number: order.order_number || null,
      delivered_at: order.delivered_at || null,
      date: formattedTimestamp,
      amount: pricingSummary.total,
      summary: pricingSummary,
      zoho_invoice_available:
        typeof order.zoho_invoice_available === "boolean"
          ? order.zoho_invoice_available
          : Number(order.zoho_invoice_available) === 1,
      zoho_invoice_number: order.zoho_invoice_number || null,
      zoho_invoice_status: order.zoho_invoice_status || null,
      status: getReadableStatus(normalizedStatus),
      statusKey: normalizedStatus,
      items: orderItems.length,
      trackingId:
        order.courier_tracking_number ||
        order.order_number ||
        `#${order.order_id}`,
      carrier: getAccountCarrierLabel(order),
      paymentMethod: getAccountPaymentLabel(order),
      shippingAddress: getAccountShippingAddress(order),
      sellerNames: order.seller_names || "",
      timelineDisplay: mapOrderTimeline(
        order.status_timeline,
        normalizedStatus,
        order.created_at
      ),
      timeline: [
        {
          status: "Order Placed",
          date: formattedTimestamp,
          completed: true,
          icon: "🛒",
        },
        {
          status: "Processing",
          date: formattedTimestamp,
          completed: hasReachedProcessingStage(normalizedStatus),
          icon: "⚙️",
        },
        {
          status: "Shipped",
          date: formattedTimestamp,
          completed: hasReachedShipmentStage(normalizedStatus),
          icon: "🚚",
        },
        {
          status: "Delivered",
          date: formattedTimestamp,
          completed: normalizedStatus === "delivered",
          icon: "🏠",
        },
      ],
      itemsDetails: orderItems.map((item) => ({
        ...(() => {
          const itemStatusKey = normalizeStatusKey(
            item.item_status || normalizedStatus
          );
          const reviewOverride =
            reviewOverrides[
              getReviewOverrideKey(
                order.order_id,
                item.item_id,
                item.product_id
              )
            ] || null;
          const normalizedReviewItem = {
            ...item,
            item_id: item.item_id,
            status: item.status || order.status || order.display_status || null,
            order_status: item.order_status || order.order_status || null,
            delivery_status: item.delivery_status || order.delivery_status || null,
            delivered_at: item.delivered_at || order.delivered_at || null,
            shipment_status: item.shipment_status || order.shipment_status || null,
          };
          const { canReview, reviewSubmitted } = resolveReviewDisplayState({
            item: normalizedReviewItem,
            order,
            reviewOverride,
          });

          return {
            item_id: item.item_id,
            product_id: item.product_id,
            name: getOrderItemName(item),
            qty: item.quantity,
            price: Number(item.total ?? item.unit_price ?? 0),
            imageUrl: getOrderItemImage(item),
            category: "Product",
            sellerName: item.seller_name || "",
            statusKey: itemStatusKey,
            statusLabel: getReadableStatus(item.item_status || normalizedStatus),
            delivered_at: normalizedReviewItem.delivered_at,
            delivery_status: normalizedReviewItem.delivery_status,
            order_status: normalizedReviewItem.order_status,
            shipment_status: normalizedReviewItem.shipment_status,
            can_review: canReview,
            review_submitted: reviewSubmitted,
            review_id: reviewOverride?.review_id ?? item.review_id ?? null,
            review_rating:
              reviewOverride?.review_rating ?? item.review_rating ?? null,
            review_status:
              reviewOverride?.review_status ?? item.review_status ?? null,
          };
        })(),
      })),
    };
  });

  const formatSyncTime = (date) => {
    if (!date) return "";
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const fetchOrders = (searchTerm = appliedOrderSearch) => {
    if (!getallorderRequest || isSyncingOrders) return;

    const trimmedSearch = String(searchTerm || "").trim();
    setIsSyncingOrders(true);
    setAppliedOrderSearch(trimmedSearch);
    getallorderRequest(trimmedSearch ? { search: trimmedSearch } : {});
  };

  const handleSyncOrders = () => {
    fetchOrders(appliedOrderSearch);
  };

  const handleOrderSearchSubmit = (e) => {
    e.preventDefault();
    fetchOrders(orderSearchInput);
  };

  const handleResetOrdersView = () => {
    setFilterStatus("all");
    setOrderSearchInput("");
    fetchOrders("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setProfileErrors((previous) => ({ ...previous, [name]: "" }));
    setProfileWasSubmitted(false);
  };

  const handleProfileSubmit = (e) => {
    e.preventDefault();

    const errors = {};
    for (const field of ["firstname", "lastname", "phone", "email"]) {
      if (!profileForm[field].trim()) errors[field] = "This field is required.";
    }
    if (!errors.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.email.trim())) {
      errors.email = "Enter a valid email address.";
    }
    setProfileErrors(errors);
    if (Object.keys(errors).length) {
      return;
    }
    setProfileWasSubmitted(true);
    insertprofileRequest(profileForm);
  };

  const resetChangePasswordForm = () => {
    setChangePasswordForm({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordErrors({});
  };

  const handleChangePasswordField = (e) => {
    const { name, value } = e.target;
    setChangePasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPasswordErrors((previous) => ({ ...previous, [name]: "", form: "" }));
  };

  const handleSubmitChangePassword = async (e) => {
    e.preventDefault();

    const errors = {};
    for (const field of ["oldPassword", "newPassword", "confirmPassword"]) {
      if (!changePasswordForm[field]) errors[field] = "This field is required.";
    }
    if (!errors.newPassword && !PASSWORD_REGEX.test(changePasswordForm.newPassword)) {
      errors.newPassword = PASSWORD_VALIDATION_MESSAGE;
    }
    if (!errors.confirmPassword && changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }
    setPasswordErrors(errors);
    if (Object.keys(errors).length) {
      return;
    }

    try {
      setIsChangingPassword(true);
      const response = await axiosInstance.post("change-password", {
        oldPassword: changePasswordForm.oldPassword,
        newPassword: changePasswordForm.newPassword,
      });

      showSuccess(response?.data?.message || "Password changed successfully");
      setShowChangePasswordModal(false);
      resetChangePasswordForm();
    } catch (error) {
      setPasswordErrors({ form: error.response?.data?.message || "Failed to change password" });
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Return product handlers
  const handleReturnClick = (order, item) => {
    setSelectedReturnItem({
      order_id: order.order_id,
      order_number: order.id,
      item_id: item.item_id,
      product_id: item.product_id,
      product_name: item.name,
      quantity: item.qty,
      price: item.price
    });
    setShowReturnModal(true);
  };

  const handleReturnFormChange = (e) => {
    const { name, value } = e.target;
    setReturnFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const imageUrls = files.map(file => URL.createObjectURL(file));
      setReturnImages(prev => [...prev, ...imageUrls]);
      setReturnFormData(prev => ({
        ...prev,
        images: [...prev.images, ...imageUrls]
      }));
    }
  };

  const removeImage = (index) => {
    setReturnImages(prev => prev.filter((_, i) => i !== index));
    setReturnFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitReturn = async () => {
    if (!returnFormData.reason) {
      showError("Please select a reason for return");
      return;
    }
    
    if (!returnFormData.description) {
      showError("Please provide a description");
      return;
    }

    const returnPayload = {
      order_id: selectedReturnItem.order_id,
      order_item_id: selectedReturnItem.item_id,
      product_id: selectedReturnItem.product_id,
      reason: returnFormData.reason,
      description: returnFormData.description,
      images: returnFormData.images,
      return_type: returnFormData.return_type
    };

    setIsSubmittingReturn(true);

    try {
      // Replace this with your actual API endpoint
      const response = await fetch('/api/returns/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getBuyerToken()}` // Adjust based on your auth
        },
        body: JSON.stringify(returnPayload)
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess("Return request submitted successfully!");
        setShowReturnModal(false);
        // Reset form
        setReturnFormData({
          reason: "",
          description: "",
          images: [],
          return_type: "refund"
        });
        setReturnImages([]);
        setSelectedReturnItem(null);
      } else {
        showError("Failed to submit return request. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting return:", error);
      showError("An error occurred. Please try again.");
    } finally {
      setIsSubmittingReturn(false);
    }
  };

  const handleReviewTextChange = (e) => {
    const { value } = e.target;
    setReviewFormData((prev) => ({
      ...prev,
      review_text: value,
    }));
  };

  const handleReviewRatingChange = (rating) => {
    setReviewFormData((prev) => ({
      ...prev,
      rating,
    }));
  };

  const handleSubmitReview = async () => {
    if (!selectedReviewItem) {
      showError("Please select a product to review");
      return;
    }

    if (!reviewFormData.rating || reviewFormData.rating < 1 || reviewFormData.rating > 5) {
      showError("Please select a rating between 1 and 5");
      return;
    }

    const trimmedReview = String(reviewFormData.review_text || "").trim();
    if (trimmedReview.length < 3) {
      showError("Please enter at least 3 characters for your review");
      return;
    }

    if (trimmedReview.length > 1000) {
      showError("Review text must not exceed 1000 characters");
      return;
    }

    try {
      setIsSubmittingReview(true);
      const response = await axiosInstance.post("reviews", {
        order_id: selectedReviewItem.order_id,
        order_item_id: selectedReviewItem.order_item_id,
        product_id: selectedReviewItem.product_id,
        rating: reviewFormData.rating,
        review_text: trimmedReview,
      });

      const reviewData = response?.data?.data || {};
      setReviewOverrides((prev) => ({
        ...prev,
        [getReviewOverrideKey(
          selectedReviewItem.order_id,
          selectedReviewItem.order_item_id,
          selectedReviewItem.product_id
        )]: {
          can_review: false,
          review_submitted: true,
          review_id: reviewData.review_id || null,
          review_rating: reviewData.rating || reviewFormData.rating,
          review_status: reviewData.status || "pending",
        },
      }));

      showSuccess(response?.data?.message || "Review submitted successfully");
      resetReviewModal();
      fetchOrders(appliedOrderSearch);
    } catch (error) {
      showError(
        error.response?.data?.message || "Failed to submit review"
      );
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const filteredOrders =
    filterStatus === "all"
      ? orders
      : orders.filter((order) => matchesOrderFilter(order.statusKey, filterStatus));

  const handleWishlistOfferExpire = (item) => {
    if (typeof getwishlistRequest !== "function") {
      return;
    }

    const activeOffer = normalizeWishlistActiveOffer(item);
    const offerEndTime = getWishlistOfferEndTime(activeOffer);
    const refreshKey = `${
      item?.wishlist_id ?? item?.product_id ?? item?.id ?? "wishlist"
    }:${activeOffer?.offer_id ?? 0}:${offerEndTime ?? ""}`;

    if (wishlistExpireRefreshesRef.current.has(refreshKey)) {
      return;
    }

    wishlistExpireRefreshesRef.current.add(refreshKey);
    getwishlistRequest();
  };

  const renderMainContent = () => {
    const toggleOrderDetails = (orderId) => {
      setExpandedOrder(expandedOrder === orderId ? null : orderId);
    };

    const handleViewOrder = (order) => {
      if (!order?.order_id) return;
      navigate(`/account/orders/${order.order_id}`, {
        state: {
          orderId: order.order_id,
          orderNumber: order.id,
          total: order.amount,
          tracking_number: order.trackingId,
        },
      });
    };

    const onclickcart = (data) => {
      if (!isUserLoggedIn()) {
        openAuthModal();
        return;
      }

      if (isProductOutOfStock(data)) {
        showError("This product is currently out of stock.");
        return;
      }

      const activeOffer = normalizeWishlistActiveOffer(data);
      const payload = {
        product_id: data?.id ?? data?.product_id,
        quantity: data?.quantity ? Number(data.quantity) : 1,
        price: Number(data?.price ?? data?.product_price ?? 0),
        discount: Number(data?.discount ?? data?.product_discount ?? 0),
        attributes: data.attributes,
      };

      if (activeOffer?.offer_id) {
        payload.offer_id = activeOffer.offer_id;
      }

      insertCartRequest(payload);
    };

    const getStatusColor = (status) => {
      switch (status.toLowerCase()) {
        case "delivered":
          return "#10b981";
        case "processing":
          return "#3b82f6";
        case "shipped":
          return "#8b5cf6";
        case "cancelled":
          return "#ef4444";
        case "pending":
          return "#f59e0b";
        default:
          return "#6b7280";
      }
    };

    switch (cardName) {
      case "orders":
        return (
          <MyOrders
            showIntro={false}
            orders={allorderdata}
            loading={getallorderloading}
            error={getallordererror}
            onRefresh={() => getallorderRequest()}
            insertCartRequest={insertCartRequest}
          />
        );

      case "profile":
        return (
          <>
            <section className="acct-card acct-profile-top">
              <div className="acct-section-heading">
                <span className="acct-section-icon"><FiUser /></span>
                <div><h2>Personal information</h2><p>Account details used for orders, communication and delivery.</p></div>
              </div>
              <form className={`acct-info-form ${isMobile ? 'mobile-profile-form' : ''}`} onSubmit={handleProfileSubmit} noValidate>
                <div className="acct-field">
                  <label htmlFor="account-firstname">First name</label>
                  <input
                    id="account-firstname"
                    name="firstname"
                    value={profileForm.firstname}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    aria-invalid={Boolean(profileErrors.firstname)}
                    aria-describedby={profileErrors.firstname ? "account-firstname-error" : undefined}
                  />
                  {profileErrors.firstname && <span className="acct-field-error" id="account-firstname-error">{profileErrors.firstname}</span>}
                </div>

                <div className="acct-field">
                  <label htmlFor="account-lastname">Last name</label>
                  <input
                    id="account-lastname"
                    name="lastname"
                    value={profileForm.lastname}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    aria-invalid={Boolean(profileErrors.lastname)}
                    aria-describedby={profileErrors.lastname ? "account-lastname-error" : undefined}
                  />
                  {profileErrors.lastname && <span className="acct-field-error" id="account-lastname-error">{profileErrors.lastname}</span>}
                </div>

                <div className="acct-field">
                  <label htmlFor="account-phone">Phone number</label>
                  <div className={`acct-profile-phone${profileErrors.phone ? " is-invalid" : ""}`}><span className="acct-phone-country" aria-hidden="true"><span className="acct-uae-flag" /></span><input id="account-phone" name="phone" type="tel" value={profileForm.phone} onChange={handleChange} placeholder="+971 5X XXX XXXX" aria-invalid={Boolean(profileErrors.phone)} aria-describedby={profileErrors.phone ? "account-phone-error" : undefined} /></div>
                  {profileErrors.phone && <span className="acct-field-error" id="account-phone-error">{profileErrors.phone}</span>}
                </div>

                <div className="acct-field">
                  <label htmlFor="account-email">Email</label>
                  <input
                    id="account-email"
                    name="email"
                    type="email"
                    value={profileForm.email}
                    onChange={handleChange}
                    placeholder="Enter email"
                    aria-invalid={Boolean(profileErrors.email)}
                    aria-describedby={profileErrors.email ? "account-email-error" : undefined}
                  />
                  {profileErrors.email && <span className="acct-field-error" id="account-email-error">{profileErrors.email}</span>}
                </div>

                <div className="acct-form-actions">
                  <button type="submit" className="acct-btn-update" disabled={insertprofileloading}>
                    {insertprofileloading ? "Saving..." : "Save changes"}
                  </button>
                  <button
                    type="button"
                    className="acct-btn-secondary"
                    onClick={() => setShowChangePasswordModal(true)}
                  >
                    Change Password
                  </button>
                </div>
                {profileWasSubmitted && insertprofiledata && !insertprofileloading && <p className="acct-form-feedback is-success" role="status">{insertprofiledata}</p>}
                {profileWasSubmitted && insertprofileerror && <p className="acct-form-feedback is-error" role="alert">{insertprofileerror}</p>}
              </form>
            </section>
            {renderSavedAddresses()}
          </>
        );

      case "addresses":
        return renderSavedAddresses();

      case "wishlist":
        return (
          <div className={`acct-wishlist-section ${isMobile ? 'mobile-wishlist' : ''}`}>
            <div className="acct-welcome-card acct-card">
              <div className={`wishlist-header ${isMobile ? 'mobile-wishlist-header' : ''}`}>
                <h2 className="wishlist-title">My Wishlist</h2>
                <p className="wishlist-subtitle">
                  {wishlistData && wishlistData.length}{" "}
                  {wishlistData && wishlistData.length === 1 ? "item" : "items"}{" "}
                  saved
                </p>
              </div>

              {wishlistData && wishlistData.length === 0 ? (
                <div className="empty-wishlist">
                  <div className="empty-wishlist-icon">
                    <svg
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                    </svg>
                  </div>
                  <p className="empty-message">Your wishlist is empty.</p>
                  <p className="empty-subtext">Save items you love for later</p>
                </div>
              ) : (
                <div className="wishlist-container">
                  <div className={`wishlist-grid ${isMobile ? 'mobile-wishlist-grid' : ''}`}>
                    {wishlistData &&
                      wishlistData.map((item) => {
                        const activeOffer = normalizeWishlistActiveOffer(item);
                        const offerEndTime = getWishlistOfferEndTime(activeOffer);
                        const price = Number(item?.product_price || 0);
                        const discount = Number(item?.product_discount || 0);
                        const imageUrl = withDefaultProductImage(
                          item.product_image ||
                            item.image_url ||
                            item.product_image_key
                        );
                        const hasActiveOffer = Boolean(activeOffer && offerEndTime);
                        const discountedPrice = hasActiveOffer
                          ? Number(activeOffer.offer_price || 0)
                          : Math.max(price - (price * discount) / 100, 0);
                        const originalPrice = hasActiveOffer
                          ? Number(activeOffer.original_price || 0)
                          : price;
                        const savedAmount = hasActiveOffer
                          ? Number(
                              activeOffer.offer_discount_amount ||
                                Math.max(originalPrice - discountedPrice, 0),
                            )
                          : 0;
                        const outOfStock = isProductOutOfStock(item);

                        return (
                          <div
                            key={item.wishlist_id ?? item.product_id ?? item.id}
                            className={`wishlist-item ${isMobile ? 'mobile-wishlist-item' : ''}`}
                          >
                            <div className="wishlist-item-main">
                              <div className="wishlist-item-image">
                                <img
                                  src={imageUrl}
                                  alt={item.product_name}
                                  className="wishlist-product-img"
                                  onError={(event) => {
                                    event.currentTarget.onerror = null;
                                    event.currentTarget.src =
                                      DEFAULT_PRODUCT_IMAGE;
                                  }}
                                />
                                <button
                                  className="item-remove-btn"
                                  title="Remove item"
                                  onClick={() => onclickremove(item)}
                                >
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                  >
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                  </svg>
                                </button>
                              </div>

                              <div className="wishlist-item-content">
                                <h3 className="wishlist-item-name">
                                  {item.product_name}
                                </h3>
                                {hasActiveOffer ? (
                                  <div className="wishlist-item-note">
                                    Save {formatAED(savedAmount)}
                                  </div>
                                ) : discount > 0 ? (
                                  <div className="wishlist-item-note">
                                    {discount}% OFF
                                  </div>
                                ) : null}
                                {hasActiveOffer ? (
                                  <div className="wishlist-offer-countdown wishlist-item-countdown">
                                    <OfferCountdown
                                      endTime={offerEndTime}
                                      expiredText="Offer expired"
                                      showZeroDays
                                      onExpire={() => handleWishlistOfferExpire(item)}
                                    />
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            <div className="wishlist-item-side">
                              <div className="wishlist-item-price-block">
                                {hasActiveOffer ? (
                                  <span className="wishlist-original-price">
                                    {formatAED(originalPrice)}
                                  </span>
                                ) : discount > 0 ? (
                                  <span className="wishlist-original-price">
                                    {formatAED(price)}
                                  </span>
                                ) : null}
                                <span className="wishlist-current-price">
                                  {formatAED(discountedPrice)}
                                </span>
                              </div>
                              <div className={`wishlist-item-actions ${isMobile ? 'mobile-item-actions' : ''}`}>
                                <button
                                  className="btn btn-primary-wishlist btn-sm"
                                  disabled={outOfStock}
                                  style={outOfStock ? { opacity: 0.7, cursor: "not-allowed" } : undefined}
                                  onClick={() => {
                                    onclickcart(item);
                                  }}
                                >
                                  {outOfStock ? "Out of Stock" : "Add to Cart"}
                                </button>
                                <button
                                  className="btn btn-primary-wishlist-remove btn-sm"
                                  onClick={() => {
                                    onclickremove(item);
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
 
      default:
        return (
          <div className="acct-welcome-card">
            <h2>Welcome to Your Account</h2>
            <p>Select a section from the sidebar to view details.</p>
          </div>
        );
    }
  };

  const onclickremove = (item) => {
    removewishlistrequest(item.product_id);
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (value, country) => {
    setAddressForm((prev) => ({
      ...prev,
      phone: value,
    }));
  };

  const onclickaddress = async () => {
    const { address_line1, city, state, pincode, address_line2, phone } = addressForm;
    
    if (!address_line1 || !city || !state || !pincode) {
      showError("Please fill all required fields");
      return;
    }

    if (!/^\d{6}$/.test(pincode)) {
      showError("Enter valid 6-digit pincode");
      return;
    }

    if (!phone) {
      showError("Phone number is required");
      return;
    }

    if (phone.length < 8) {
      showError("Please enter a valid phone number");
      return;
    }

    const params = {
      address: address_line1 + (address_line2 ? " " + address_line2 : ""),
      city,
      state,
      pincode,
      phone: phone,
    };
    setAddressActionError("");
    if (editingAddressId) {
      try {
        setIsAddressSaving(true);
        await axiosInstance.put(`addresses/${editingAddressId}`, params);
        setAddressRows((previous) => previous.map((address) => Number(address.profile_id) === editingAddressId ? { ...address, ...params, alt_phone: params.phone } : address));
        showSuccess("Address updated successfully");
        onclickcancel();
        getaddressRequest();
      } catch (error) {
        setAddressActionError(error.response?.data?.message || "Unable to update address.");
      } finally {
        setIsAddressSaving(false);
      }
      return;
    }
    pendingAddressRef.current = params;
    addAddressRequest(params);
  };

  const onclickcancel = () => {
    setShowAddAddress(false);
    setEditingAddressId(null);
    setAddressActionError("");
    pendingAddressRef.current = null;
    setAddressForm({
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      pincode: "",
      phone: "",
    });
  };

  const openAddAddress = () => {
    onclickcancel();
    setShowAddAddress(true);
  };

  const openEditAddress = (address) => {
    setEditingAddressId(Number(address.profile_id));
    setAddressActionError("");
    setAddressForm({
      address_line1: address.address || "",
      address_line2: "",
      city: address.city || "",
      state: address.state || "",
      pincode: address.pincode || "",
      phone: address.alt_phone || address.phone || "",
    });
    setShowAddAddress(true);
  };

  const removeAddress = async (address) => {
    if (!window.confirm("Remove this saved address?")) return;
    const id = Number(address.profile_id);
    setAddressActionId(id);
    setAddressActionError("");
    try {
      await axiosInstance.delete(`addresses/${id}`);
      setAddressRows((previous) => {
        const remaining = previous.filter((entry) => Number(entry.profile_id) !== id);
        if (Number(address.is_default_address) === 1 && remaining.length) {
          const nextDefaultId = Number(remaining[remaining.length - 1].profile_id);
          return remaining.map((entry) => ({ ...entry, is_default_address: Number(entry.profile_id) === nextDefaultId ? 1 : 0 }));
        }
        return remaining;
      });
      showSuccess("Address removed successfully");
      getaddressRequest();
    } catch (error) {
      setAddressActionError(error.response?.data?.message || "Unable to remove address.");
    } finally {
      setAddressActionId(null);
    }
  };

  const setDefaultAddress = async (address) => {
    const id = Number(address.profile_id);
    setAddressActionId(id);
    setAddressActionError("");
    try {
      await axiosInstance.post("selectDeliveryAddress", { addressId: id });
      setAddressRows((previous) => previous.map((entry) => ({ ...entry, is_default_address: Number(entry.profile_id) === id ? 1 : 0 })));
      showSuccess("Default address updated");
      getaddressRequest();
    } catch (error) {
      setAddressActionError(error.response?.data?.message || "Unable to set the default address.");
    } finally {
      setAddressActionId(null);
    }
  };

  const renderSavedAddresses = () => (
    <section className="acct-card acct-saved-addresses">
      <div className="saved-address-header">
        <div className="acct-section-heading">
          <span className="acct-section-icon"><FiMapPin /></span>
          <div><h2>Saved addresses</h2><p>Manage your delivery addresses for a faster checkout experience.</p></div>
        </div>
        <button type="button" className="add-address-link" onClick={openAddAddress}><FiPlus /> Add new address</button>
      </div>
      {addressActionError && <p className="acct-form-feedback is-error" role="alert">{addressActionError}</p>}
      {getaddressError && !addressRows.length && <p className="acct-form-feedback is-error" role="alert">Unable to load saved addresses.</p>}
      {getaddressLoading && !getaddressData && !addressRows.length ? <div className="acct-no-address-box" role="status">Loading addresses...</div> : addressRows.length ? (
        <div className="saved-address-wrapper">
          {addressRows.map((address) => {
            const id = Number(address.profile_id);
            const isDefault = Number(address.is_default_address) === 1;
            const label = address.label || address.address_label || address.address_type || "Address";
            const locality = [...new Set([address.city, address.state].filter(Boolean))].concat(address.pincode ? [address.pincode] : []).join(", ");
            const phone = address.alt_phone || address.phone;
            return <article className="saved-address-card" key={id}>
              <div className="saved-address-main">
                <span className="saved-address-type-icon">{String(label).toLowerCase() === "home" ? <FiHome /> : String(label).toLowerCase() === "office" ? <FiBriefcase /> : <FiMapPin />}</span>
                <div className="saved-address-content">
                  <div className="saved-address-name"><strong>{label}</strong>{isDefault && <span className="saved-address-default">Default</span>}</div>
                  <p className="address-text">{address.address}</p>
                  {locality && <p className="address-sub">{locality}</p>}
                  {address.country && <p className="address-sub">{address.country}</p>}
                </div>
              </div>
              {phone && <p className="address-phone"><FiPhone /> {phone}</p>}
              <div className="saved-address-actions">
                <button type="button" onClick={() => openEditAddress(address)} disabled={addressActionId !== null}><FiEdit2 /> Edit</button>
                <button type="button" className="is-remove" onClick={() => removeAddress(address)} disabled={addressActionId !== null}><FiTrash2 /> Remove</button>
                {!isDefault && <button type="button" className="is-default" onClick={() => setDefaultAddress(address)} disabled={addressActionId !== null}><FiStar /> Set as default</button>}
              </div>
            </article>;
          })}
        </div>
      ) : <div className="acct-no-address-box"><p>No saved addresses yet.</p><button type="button" onClick={openAddAddress}>Add new address</button></div>}
    </section>
  );

  const pageTitle = { orders: "My Orders", profile: "My Profile", wishlist: "Wishlist", addresses: "Saved Addresses" }[cardName] || "My Account";
  const pageDescription = {
    orders: "Track, manage and reorder your medical supplies.",
    profile: "Keep your contact details and delivery addresses up to date.",
    wishlist: "Your saved products, ready when you need them.",
    addresses: "Manage your delivery addresses for a faster checkout experience.",
  }[cardName] || "";

  return (
    <div className={`acct-page acct-page--horizontal ${cardName === 'orders' ? 'acct-page--orders' : ''} ${isMobile ? 'mobile-account-page' : ''}`}>
      <div className={`acct-inner ${isMobile ? 'mobile-inner' : ''}`}>
        <header className="acct-page-heading">
          <div className="acct-breadcrumb"><button type="button" onClick={() => navigate("/")}>Home</button><span>/</span><span>My Account</span><span>/</span><strong>{pageTitle}</strong></div>
          <div className="acct-page-title-row"><div><h1>{pageTitle}</h1><p>{pageDescription}</p></div>{cardName === "orders" && <button type="button" className="acct-orders-refresh" onClick={() => getallorderRequest()} disabled={getallorderloading} aria-label="Refresh orders"><FiRefreshCw /></button>}</div>
        </header>
        <nav className="acct-side-nav" aria-label="Account sections" ref={accountNavRef}>
            {sidebarItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`acct-nav-item ${cardName === item.key ? "acct-active" : ""
                  } ${isMobile ? 'mobile-nav-item' : ''}`}
                aria-current={cardName === item.key ? "page" : undefined}
                onClick={() => handleSidebarItemClick(item.key)}
              >
                <span className="acct-nav-icon">{item.icon}</span>
                <span className="acct-nav-title">{item.title}</span>
              </button>
            ))}
        </nav>

        <main className={`acct-main ${isMobile ? 'mobile-main' : ''}`}>
          {showKYC ? (
            <KYC onBack={() => setShowKYC(false)} />
          ) : (
            renderMainContent()
          )}
        </main>
      </div>
      
      <Modal
        isOpen={showLogoutConfirm}
        onClose={handleCancelLogout}
        maxWidth="420px"
        ariaLabel="Logout confirmation"
      >
        <div className="modal-shell modal-shell--centered modal-shell--compact">
          <div className="modal-header modal-header--centered">
            <h3 className="modal-title">Logout</h3>
            <p className="modal-subtitle">
              Are you sure you want to logout?
            </p>
          </div>
          <div className="modal-actions modal-actions--centered">
            <button
              type="button"
              className="modal-button modal-button--secondary"
              onClick={handleCancelLogout}
            >
              No
            </button>
            <button
              type="button"
              className="modal-button modal-button--primary"
              onClick={handleConfirmLogout}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        onClose={resetReviewModal}
        isOpen={showReviewModal}
        maxWidth="520px"
      >
        <div className={`review-modal-container ${isMobile ? "mobile-review-modal" : ""}`}>
          <h3 className="review-modal-title">Add Review</h3>

          {selectedReviewItem && (
            <div className="review-product-summary">
              <div className="review-product-name">
                {selectedReviewItem.product_name}
              </div>
              <div className="review-product-order">
                Order #{selectedReviewItem.order_number}
              </div>
            </div>
          )}

          <div className="review-form-group">
            <label className="review-form-label required">Rating</label>
            <div className="review-stars-row" role="radiogroup" aria-label="Rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`review-star-btn ${reviewFormData.rating >= star ? "selected" : ""}`}
                  onClick={() => handleReviewRatingChange(star)}
                  aria-label={`${star} star${star > 1 ? "s" : ""}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          <div className="review-form-group">
            <label className="review-form-label required">Description</label>
            <textarea
              className="review-textarea"
              rows="5"
              maxLength="1000"
              placeholder="Write your review"
              value={reviewFormData.review_text}
              onChange={handleReviewTextChange}
            />
            <div className="review-character-count">
              {reviewFormData.review_text.length}/1000
            </div>
          </div>

          <div className="review-modal-actions">
            <button
              type="button"
              className="address-btn-secondary"
              onClick={resetReviewModal}
              disabled={isSubmittingReview}
            >
              Cancel
            </button>
            <button
              type="button"
              className="address-btn-primary"
              onClick={handleSubmitReview}
              disabled={isSubmittingReview}
            >
              {isSubmittingReview ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        onClose={() => {
          setShowChangePasswordModal(false);
          resetChangePasswordForm();
        }}
        isOpen={showChangePasswordModal}
      >
        <div className={`address-form-container ${isMobile ? 'mobile-address-modal' : ''}`}>
          <h3 className="address-form-title">Change Password</h3>

          <form onSubmit={handleSubmitChangePassword}>
            <div className="address-form-group">
              <label className="address-form-label required" htmlFor="account-old-password">
                Old Password
              </label>
              <input
                id="account-old-password"
                type="password"
                name="oldPassword"
                className="address-form-input"
                placeholder="Enter your current password"
                value={changePasswordForm.oldPassword}
                onChange={handleChangePasswordField}
                autoComplete="current-password"
                aria-invalid={Boolean(passwordErrors.oldPassword)}
              />
              {passwordErrors.oldPassword && <div className="acct-field-error">{passwordErrors.oldPassword}</div>}
            </div>

            <div className="address-form-group">
              <label className="address-form-label required" htmlFor="account-new-password">
                New Password
              </label>
              <input
                id="account-new-password"
                type="password"
                name="newPassword"
                className="address-form-input"
                placeholder="Enter your new password"
                value={changePasswordForm.newPassword}
                onChange={handleChangePasswordField}
                autoComplete="new-password"
                aria-invalid={Boolean(passwordErrors.newPassword)}
              />
              {passwordErrors.newPassword && <div className="acct-field-error">{passwordErrors.newPassword}</div>}
              <div className="address-form-helper">
                Password must include letters, numbers and a special character.
              </div>
            </div>

            <div className="address-form-group">
              <label className="address-form-label required" htmlFor="account-confirm-password">
                Confirm New Password
              </label>
              <input
                id="account-confirm-password"
                type="password"
                name="confirmPassword"
                className="address-form-input"
                placeholder="Confirm your new password"
                value={changePasswordForm.confirmPassword}
                onChange={handleChangePasswordField}
                autoComplete="new-password"
                aria-invalid={Boolean(passwordErrors.confirmPassword)}
              />
              {passwordErrors.confirmPassword && <div className="acct-field-error">{passwordErrors.confirmPassword}</div>}
            </div>

            <div className={`address-form-buttons ${isMobile ? 'mobile-address-buttons' : ''}`}>
              {passwordErrors.form && <p className="acct-form-feedback is-error" role="alert">{passwordErrors.form}</p>}
              <button
                type="button"
                className="address-btn-secondary"
                onClick={() => {
                  setShowChangePasswordModal(false);
                  resetChangePasswordForm();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="address-btn-primary"
                disabled={isChangingPassword}
              >
                {isChangingPassword ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal onClose={() => { if (!addAddressLoading && !isAddressSaving) onclickcancel(); }} isOpen={showAddAddress}>
        <div className={`address-form-container ${isMobile ? 'mobile-address-modal' : ''}`}>
          <h3 className="address-form-title">{editingAddressId ? "Edit address" : "Add new address"}</h3>

          <div className="address-form-group">
            <label className="address-form-label required" htmlFor="account-address-line1">
              Address Line 1
            </label>
            <input
              id="account-address-line1"
              type="text"
              name="address_line1"
              className="address-form-input"
              placeholder="House No, Building, Street"
              value={addressForm.address_line1}
              onChange={handleAddressChange}
            />
          </div>

          <div className="address-form-group">
            <label className="address-form-label" htmlFor="account-address-line2">Address Line 2</label>
            <input
              id="account-address-line2"
              type="text"
              name="address_line2"
              className="address-form-input"
              placeholder="Area, Locality, Landmark"
              value={addressForm.address_line2}
              onChange={handleAddressChange}
            />
          </div>

          <div className={`address-form-grid ${isMobile ? 'mobile-address-grid' : ''}`}>
            <div className="address-form-group">
              <label className="address-form-label required" htmlFor="account-address-city">City</label>
              <select
                id="account-address-city"
                name="city"
                className="address-form-input"
                value={addressForm.city}
                onChange={handleAddressChange}
                disabled={!addressForm.state}
              >
                <option value="">Select city</option>
                {getCitiesForEmirate(addressForm.state).map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
                {addressForm.city && !getCitiesForEmirate(addressForm.state).includes(addressForm.city) && <option value={addressForm.city}>{addressForm.city}</option>}
              </select>
            </div>

            <div className="address-form-group">
              <label className="address-form-label required" htmlFor="account-address-state">Emirate</label>
              <select
                id="account-address-state"
                name="state"
                className="address-form-input"
                value={addressForm.state}
                onChange={(e) => {
                  const emirate = e.target.value;
                  const cities = getCitiesForEmirate(emirate);
                  setAddressForm((prev) => ({
                    ...prev,
                    state: emirate,
                    city: cities[0] || "",
                  }));
                }}
              >
                <option value="">Select emirate</option>
                {UAE_EMIRATES.map((emirate) => (
                  <option key={emirate} value={emirate}>
                    {emirate}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="address-form-group">
            <label className="address-form-label required" htmlFor="account-address-pincode">Pincode</label>
            <input
              id="account-address-pincode"
              type="text"
              name="pincode"
              className="address-form-input"
              placeholder="6-digit pincode"
              value={addressForm.pincode}
              onChange={handleAddressChange}
            />
          </div>

          <div className="address-form-group">
            <label className="address-form-label required" htmlFor="account-address-phone">Phone Number</label>
            <PhoneInput
              inputProps={{ id: "account-address-phone" }}
              country={'ae'}
              value={addressForm.phone}
              onChange={handlePhoneChange}
              inputStyle={{
                width: '100%',
                height: '45px',
                fontSize: '14px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                paddingLeft: '58px'
              }}
              buttonStyle={{
                backgroundColor: '#f8f8f8',
                border: '1px solid #ddd',
                borderRight: 'none',
                borderRadius: '8px 0 0 8px'
              }}
              dropdownStyle={{
                borderRadius: '8px',
                border: '1px solid #ddd'
              }}
              searchStyle={{
                borderRadius: '6px',
                border: '1px solid #ddd'
              }}
              preferredCountries={['ae', 'in', 'us', 'gb']}
              enableSearch={true}
              searchPlaceholder="Search country"
              autoFormat={false}
              enableAreaCodes={true}
              enableAreaCodeStretch={true}
              countryCodeEditable={true}
              disableCountryCode={false}
              disableAreaCodes={false}
              international={true}
              defaultCountry={'ae'}
            />
          </div>

          <div className={`address-form-buttons ${isMobile ? 'mobile-address-buttons' : ''}`}>
            {addressActionError && <p className="acct-form-feedback is-error" role="alert">{addressActionError}</p>}
            <button
              type="button"
              className="address-btn-secondary"
              onClick={() => onclickcancel()}
              disabled={addAddressLoading || isAddressSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="address-btn-primary"
              onClick={onclickaddress}
              disabled={addAddressLoading || isAddressSaving}
            >
              {addAddressLoading || isAddressSaving ? "Saving..." : editingAddressId ? "Save address" : "Add address"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Return Product Modal */}
      <Modal onClose={() => setShowReturnModal(false)} isOpen={showReturnModal}>
        <div className={`return-form-container ${isMobile ? 'mobile-return-modal' : ''}`}>
          <h3 className="return-form-title">Return Product</h3>
          
          {selectedReturnItem && (
            <div className="return-product-info">
              <p><strong>Product:</strong> {selectedReturnItem.product_name}</p>
              <p><strong>Quantity:</strong> {selectedReturnItem.quantity}</p>
              <p><strong>Price:</strong> AED {selectedReturnItem.price.toFixed(2)}</p>
            </div>
          )}

          <div className="return-form-group">
            <label className="return-form-label required">Reason for Return</label>
            <select
              name="reason"
              className="return-form-input"
              value={returnFormData.reason}
              onChange={handleReturnFormChange}
              required
            >
              <option value="">Select a reason</option>
              <option value="Damaged">Damaged Product</option>
              <option value="Wrong Item">Wrong Item Received</option>
              <option value="Size Issue">Size Issue</option>
              <option value="Defective">Defective Product</option>
              <option value="Not as described">Not as described</option>
              <option value="Changed mind">Changed mind</option>
            </select>
          </div>

          <div className="return-form-group">
            <label className="return-form-label required">Description</label>
            <textarea
              name="description"
              className="return-form-textarea"
              rows="4"
              placeholder="Please provide detailed description of the issue..."
              value={returnFormData.description}
              onChange={handleReturnFormChange}
              required
            />
          </div>

          <div className="return-form-group">
            <label className="return-form-label">Upload Images (Optional)</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="return-file-input"
            />
            {returnImages.length > 0 && (
              <div className="return-images-preview">
                {returnImages.map((img, idx) => (
                  <div key={idx} className="return-image-item">
                    <img src={img} alt={`Return evidence ${idx + 1}`} />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => removeImage(idx)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="return-form-group">
            <label className="return-form-label">Return Type</label>
            <div className="return-type-options">
              <label className="return-type-label">
                <input
                  type="radio"
                  name="return_type"
                  value="refund"
                  checked={returnFormData.return_type === "refund"}
                  onChange={handleReturnFormChange}
                />
                Refund
              </label>
              <label className="return-type-label">
                <input
                  type="radio"
                  name="return_type"
                  value="exchange"
                  checked={returnFormData.return_type === "exchange"}
                  onChange={handleReturnFormChange}
                />
                Exchange
              </label>
            </div>
          </div>

          <div className={`return-form-buttons ${isMobile ? 'mobile-return-buttons' : ''}`}>
            <button
              type="button"
              className="return-btn-secondary"
              onClick={() => {
                setShowReturnModal(false);
                setReturnFormData({
                  reason: "",
                  description: "",
                  images: [],
                  return_type: "refund"
                });
                setReturnImages([]);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="return-btn-primary"
              onClick={handleSubmitReturn}
              disabled={isSubmittingReturn}
            >
              {isSubmittingReturn ? "Submitting..." : "Submit Return Request"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
